/**
 * Lightweight client-side distance / drive-time math used to thread the
 * user's chosen ZIP through Explore filters, bubbles, and ranking without
 * any per-event API calls.
 *
 * We use a haversine straight-line distance, multiply by a routing factor
 * (real roads zig-zag), and divide by a calibrated effective speed for our
 * service area (SR-70 / I-75 / US-41 corridor). Bubbles always round UP to
 * the next 5-minute increment so we under-promise rather than over-promise.
 * Real directions still come from the Google Maps deep-link on each card.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Lakewood Ranch (34202) — the baseline our stored drive_minutes_from_lwr uses. */
export const LWR_CENTER: LatLng = { lat: 27.4015, lng: -82.3959 };

/**
 * Calibration: real Florida driving (lights, bridges, US-41 traffic) is
 * slower and longer than great-circle math. Using ~30 mph effective with a
 * 1.25× routing detour factor gives roughly Google-Maps-accurate numbers
 * for our service area (e.g. 34211 → Osprey/Spanish Point ≈ 35 min, which
 * matches Google's typical 30–40 min depending on traffic).
 */
const AVG_MPH = 30;
const ROUTING_FACTOR = 1.25;

/** Round a minute count to the NEAREST multiple of 5 (min 5). */
export function roundTo5(mins: number): number {
  return Math.max(5, Math.round(mins / 5) * 5);
}

/** Backwards-compat alias — older imports referenced ceilTo5. */
export const ceilTo5 = roundTo5;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance in miles between two coordinates. */
export function haversineMiles(a: LatLng, b: LatLng): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Estimated one-way drive time in minutes from a straight-line mile distance.
 * Always rounded UP to the next 5-minute increment.
 */
export function estimateDriveMinutes(miles: number): number {
  const mins = ((miles * ROUTING_FACTOR) / AVG_MPH) * 60;
  return roundTo5(mins);
}

/**
 * Extract a 5-digit US ZIP from a venue address string.
 *
 * Real US addresses put the ZIP at the END, after the state. A naive
 * `\b\d{5}\b` match grabs the street number first (e.g. "30171 Betts Rd,
 * Myakka City, FL 34251" → "30171"), which then geocodes to Rydal, GA and
 * blows up distance math. We anchor the match to the end of the string and
 * optionally require a 2-letter state token before it.
 */
export function extractZipFromAddress(address?: string | null): string | null {
  if (!address) return null;
  // Prefer "<STATE> <ZIP>" near the end (most reliable).
  const stateZip = address.match(/\b[A-Z]{2}\s+(\d{5})(?:-\d{4})?\b\s*(?:USA?)?\.?\s*$/i);
  if (stateZip) return stateZip[1];
  // Otherwise take the last 5-digit token in the string.
  const all = address.match(/\b\d{5}(?:-\d{4})?\b/g);
  if (all && all.length > 0) return all[all.length - 1].slice(0, 5);
  return null;
}

interface EventLike {
  lat?: number | null;
  lng?: number | null;
  venue_address?: string | null;
  drive_minutes_from_lwr?: number;
}

/**
 * Estimate drive time from `origin` to a given event.
 *
 * Preference order:
 *   1. Event has lat/lng → haversine + calibrated speed (best).
 *   2. We can resolve the venue's ZIP from its address via `zipCoords` →
 *      treat the ZIP centroid as the venue location (good enough for ranking
 *      and bubbles; ZIP centroids are typically within 1–3 miles of any
 *      address inside the ZIP).
 *   3. Final fallback: shift the stored LWR-baseline drive time by half of
 *      origin→LWR. Used only when neither coords nor a resolvable ZIP exist.
 */
export function driveMinutesFor(
  event: EventLike,
  origin: LatLng,
  zipCoords?: Map<string, LatLng>,
): number {
  if (typeof event.lat === "number" && typeof event.lng === "number") {
    return estimateDriveMinutes(haversineMiles(origin, { lat: event.lat, lng: event.lng }));
  }
  const zip = extractZipFromAddress(event.venue_address);
  if (zip && zipCoords) {
    const coords = zipCoords.get(zip);
    if (coords) {
      return estimateDriveMinutes(haversineMiles(origin, coords));
    }
  }
  const baseline = typeof event.drive_minutes_from_lwr === "number" ? event.drive_minutes_from_lwr : 15;
  const originToLwr = estimateDriveMinutes(haversineMiles(origin, LWR_CENTER));
  const adjusted = baseline + originToLwr * 0.5 - 5;
  return ceilTo5(adjusted);
}
